import PropTypes, { element, oneOfType, string } from 'prop-types'
import { Tooltip } from '@opallabs/lapidary'
import { kuiperClassNames } from '../utils'
import Action from './Action'
import Remover from './Remover'
import Title from './Title'
import TokenIcon from './TokenIcon'

import './styles.scss'

const Token = ({
  actions,
  avatared = false,
  badge,
  className,
  color,
  disabled = false,
  displayIconRight = false,
  focused = false,
  htmlId,
  icon,
  iconAssetReference,
  iconSystemName,
  onClick,
  onMouseEnter,
  onRemove,
  removable = true,
  showDeselectIcon,
  selectable = true,
  selected = false,
  style,
  styleOverrides = {},
  /**
   * The existing SCSS file is written with the dark theme as the default, so
   * the default `theme` has to match. All modern components default to the
   * light theme.
   */
  theme = 'dark',
  title,
  tooltip,
  userId,
}) => {
  const classes = kuiperClassNames(className, 'token', {
    avatared,
    disabled,
    focused,
    removable,
    selected,
    deselectable: showDeselectIcon,
    theme,
    'icon-right': displayIconRight,
    'has-badge': badge,
  })

  const tooltipContent =
    typeof tooltip === 'string' ? tooltip : (tooltip?.content ?? '')

  const mouseDownHandler = disabled ? null : onClick
  return (
    /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/no-static-element-interactions */
    <Tooltip disabled={!tooltipContent} text={tooltipContent}>
      <div
        aria-disabled={disabled}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={selectable ? 0 : -1}
        className={classes}
        css={styleOverrides.token}
        onMouseEnter={onMouseEnter}
        onMouseDown={mouseDownHandler}
      >
        <div className={kuiperClassNames('tag-punch')} />
        <TokenIcon
          styleOverrides={styleOverrides.icon}
          {...{
            userId,
            tooltip,
            title,
            icon,
            iconAssetReference,
            iconSystemName,
            color,
            style,
            avatared,
          }}
        />
        <Title
          styleOverrides={styleOverrides.title}
          {...{ tooltip, title, badge, htmlId }}
        />
        <Remover {...{ removable: removable && !actions, onRemove }} />
        {Object.keys(actions || {}).map((actionIcon) => (
          <Action
            key={actionIcon}
            icon={actionIcon}
            handler={actions[actionIcon]}
          />
        ))}
      </div>
    </Tooltip>
  )
}
Token.displayName = 'Kuiper: Token'

Token.propTypes = {
  actions: PropTypes.object,
  avatared: PropTypes.bool,
  badge: PropTypes.string,
  className: PropTypes.any,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  displayIconRight: PropTypes.bool,
  focused: PropTypes.bool,
  htmlId: PropTypes.string,
  icon: oneOfType([element, string]),
  iconAssetReference: PropTypes.object,
  iconSystemName: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onRemove: PropTypes.func,
  removable: PropTypes.bool,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  showDeselectIcon: PropTypes.bool,
  style: PropTypes.object,
  styleOverrides: PropTypes.shape({
    icon: PropTypes.object,
    token: PropTypes.object,
    title: PropTypes.object,
  }),
  theme: PropTypes.oneOf(['dark', 'light']),
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.any,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

export default Token
